import { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaUsers,
  FaLink,
} from 'react-icons/fa';
import IntegrationCard from '../../components/modules/integrations/IntegrationCard';
import './IntegrationsPage.css';
import { Header } from '../../components/layout';
import { EmptyState, Loading } from '../../components/common';
import StatsCard from '../../components/modules/overview/StatsCard/StatsCard';
import { cookieService, LocalStorageService } from '../../services/cookieService';
import { fetchStats } from '../../store/slices/statsSlice';

import {
  fetchIntegrations,
  syncIntegration,
  checkTaskStatus,
  testConnection,
} from '../../store/slices/integrationsSlice';

import { selectIntegrationsStats } from '../../store/selectors/integrationsSelectors';


const IntegrationsPage = () => {
  const dispatch = useDispatch();

  const { integrations, loading, error, refreshing } = useSelector( (state) => state.integrations );
  const integrationstats = useSelector(selectIntegrationsStats);
  const { data: stats, loading: statsLoading } = useSelector((state) => state.stats);

  const [activeIDPs, setActiveIDPs] = useState([])

  useEffect(() => {
    if (!stats?.total?.total_latest_identities) {
      dispatch(fetchStats());
    }
  }, [dispatch, stats?.total?.total_latest_identities]);
  // Initial data fetch
  useEffect(() => {
    dispatch(fetchIntegrations());
  }, [dispatch]);

  // Check for pending syncs in backend cookies on page load
  useEffect(() => {
    if (integrations?.length > 0) {
      integrations.forEach((integration) => {
        const backendStatus = LocalStorageService.getTaskStatus(integration?.id);
        const backendTaskId = LocalStorageService.getTaskId(integration?.id);

        if (backendStatus === 'PENDING' && backendTaskId) {
          // console.log(`Auto-resuming sync for ${integration.id}`);
          // This will be handled by IntegrationCard component
        }
      });
    }
  }, [integrations]);

  // ── Handlers ───────────────────────────────────────────────────

  const handleSync = useCallback(
    async (id) => {
      try {
        // console.log("id: ", id)
        const result = await dispatch(syncIntegration({integrationId: id})).unwrap();
        return result;
      } catch (err) {
        console.error('Sync failed:', err);
        throw err;
      }
    },
    [dispatch]
  );

  const handleCheckTaskStatus = useCallback(
    async (taskId, integrationId) => {
      try {
        const result = await dispatch(
          checkTaskStatus({ taskId, integrationId })
        ).unwrap();
        // console.log('Task status:', result);
        return result;
      } catch (err) {
        console.error('Task status check failed:', err);
        throw err;
      }
    },
    [dispatch]
  );

  const handleTestConnection = useCallback(
    async (id) => {
      try {
        const result = await dispatch(testConnection(id)).unwrap();
        return result;
      } catch (err) {
        console.error('Connection test failed:', err);
        throw err;
      }
    },
    [dispatch]
  );

  return (
    <div className="identities-page"> {/* identities-page class not in css - to be removed */}
      <Header
        title="Integrations"
        subtitle="Connected identity sources and synchronization status"
        showSearch={false}
        showSync={false}
        showExport={false}
      />

      <div className="integrations-content"> {/* Integrations-content class not in css - to be removed */}
        {/* Stats Section */}
        <div className="integrations-stats">
          <StatsCard
            icon={<FaLink size={17} />}
            title="Total Platforms"
            value={integrationstats?.total}
            iconColor="info"
            loading={loading}
          />
          <StatsCard
            icon={<FaCheckCircle size={17} />}
            title="Connected"
            value={integrationstats?.connected}
            iconColor="success"
            loading={loading}
          />
          <StatsCard
            icon={<FaTimesCircle size={17} />}
            title="Not Connected"
            value={integrationstats?.notConnected}
            iconColor="danger"
            loading={loading}
          />
          <StatsCard
            icon={<FaUsers size={17} />}
            title="Total Identities"
            value={stats?.total?.total_latest_identities}
            iconColor="warning"
            loading={loading}
          />
        </div>

        {/* Error Display */}
        {error && (
          <div
            className="error-banner">
            {error}
          </div>
        )}

        {/* Integrations Grid */}
        <div className="integrations-grid">
          {integrations.map((integration) => (
            <IntegrationCard
              key={integration?.id}
              integration={integration}
              onSync={handleSync}
              onCheckTask={handleCheckTaskStatus}
              onTestConnection={handleTestConnection}
              setActiveIDPs={setActiveIDPs}
            />
          ))}
        </div>

        {loading && (
          <div className="integrations-empty">
            <Loading
              size="sm"
              text="Loading integrations..."
              fullPage={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationsPage;